"use client"

import * as React from "react"
import Script from "next/script"

import { useConsent } from "@/features/visitors/hooks/use-consent"
import {
  gaPurchase,
  gaViewItem,
  gaViewItemList,
  loadAnalyticsNow,
  onAnalyticsWanted,
  watchCartForAnalytics,
  type GaLine,
  type GaOrder,
} from "@/features/visitors/lib/google-analytics"
import { afterFirstInteraction } from "@/lib/first-interaction"

const GRANTED = {
  ad_storage: "granted",
  ad_user_data: "granted",
  ad_personalization: "granted",
  analytics_storage: "granted",
}

const DENIED = {
  ad_storage: "denied",
  ad_user_data: "denied",
  ad_personalization: "denied",
  analytics_storage: "denied",
}

/**
 * GA4 through Google's consent mode: granted unless the saved choice is "denied" (then no
 * cookies, cookieless pings only). The inline script applies the saved choice (use-consent's
 * persisted store) before `config`, so it holds from the first page.
 */
export function GoogleAnalytics({ id }: { id: string }) {
  const consent = useConsent((s) => s.consent)
  const loaded = React.useRef(false)
  // gtag.js (170 KB) waits for the first interaction; the stub queues calls until then.
  // A page that must report before anyone touches it asks for it through onAnalyticsWanted.
  const [library, setLibrary] = React.useState(false)
  React.useEffect(() => afterFirstInteraction(() => setLibrary(true)), [])
  React.useEffect(() => onAnalyticsWanted(() => setLibrary(true)), [])

  React.useEffect(() => watchCartForAnalytics(), [])

  React.useEffect(() => {
    // On load the inline script already applied the saved choice.
    if (!loaded.current) {
      loaded.current = true
      return
    }
    window.gtag?.("consent", "update", consent !== "denied" ? GRANTED : DENIED)
  }, [consent])

  return (
    <>
      <Script id="google-analytics" strategy="afterInteractive">
        {`window.dataLayer = window.dataLayer || [];
function gtag(){dataLayer.push(arguments);}
window.gtag = gtag;
gtag('consent', 'default', ${JSON.stringify(GRANTED)});
try {
  var saved = JSON.parse(localStorage.getItem('skm.consent') || 'null');
  if (saved && saved.state && saved.state.consent === 'denied') {
    gtag('consent', 'update', ${JSON.stringify(DENIED)});
  }
} catch (e) {}
gtag('js', new Date());
gtag('config', ${JSON.stringify(id)});`}
      </Script>
      {library ? (
        <Script
          src={`https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(id)}`}
          strategy="afterInteractive"
        />
      ) : null}
    </>
  )
}

/** view_item, on a product or combo page. */
export function GaViewItem({
  sku,
  name,
  price,
  variant,
}: {
  sku: string
  name: string
  price: string
  variant?: string
}) {
  React.useEffect(() => {
    gaViewItem({ sku, name, variant, qty: 1, unitPrice: price })
  }, [sku, name, variant, price])
  return null
}

/** purchase, on the order confirmation; reported once per order. */
export function GaPurchase({ order }: { order: GaOrder }) {
  // A string, so a new object each render is not a change.
  const payload = JSON.stringify(order)
  React.useEffect(() => {
    // The buyer may never touch this page, so gtag.js cannot wait for an interaction.
    loadAnalyticsNow()
    gaPurchase(JSON.parse(payload) as GaOrder)
  }, [payload])
  return null
}

/** view_item_list, for a list rendered on the server. */
export function GaViewItemList({
  listId,
  listName,
  lines,
}: {
  listId: string
  listName: string
  lines: GaLine[]
}) {
  // A string, so a new array each render is not a change.
  const payload = JSON.stringify(lines)
  React.useEffect(() => {
    gaViewItemList(listId, listName, JSON.parse(payload) as GaLine[])
  }, [listId, listName, payload])
  return null
}
